import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { Layout, Menu, MenuProps, Popover } from "antd";
import { clsx } from "clsx";
import { Drawer, Switch } from "../../design-system";
import { SvgIcon } from "../../common/components/SvgIcon";
import { useMediaQuery } from "../../common/useMediaQuery";
import classes from "./AppShell.module.less";

const { Content, Sider } = Layout;

// The sider's width, which antd only takes as a prop.
const NAV_WIDTH = 200;
// The least width the stage gets. Under the two together the shell goes
// compact: the nav becomes a drawer behind a menu button, and the stage
// keeps this width, so the page scrolls sideways on a narrower window.
const STAGE_MIN_WIDTH = 1200;
const COMPACT_QUERY = `(max-width: ${NAV_WIDTH + STAGE_MIN_WIDTH - 1}px)`;

// antd's `items` API in place of `<Menu.Item>` children, which it deprecates.
export type AppShellMenuItem = Required<MenuProps>["items"][number];

// A labelled, collapsible section of nav entries: uppercase header above one
// rounded block holding the entries. `pinBottom` sends it down with the
// account rows instead of following the entries above it.
export const navSection = (
    key: string,
    label: React.ReactNode,
    children: AppShellMenuItem[],
    options: { pinBottom?: boolean } = {}
): AppShellMenuItem => ({
    key,
    className: clsx(classes.navGroup, classes.section, options.pinBottom && classes.accountGroup),
    label,
    children,
});

// An unlabelled block of nav entries, for a nav with no sections.
export const navGroup = (key: string, children: AppShellMenuItem[]): AppShellMenuItem => ({
    key,
    type: "group",
    className: classes.navGroup,
    children,
});

// An unlabelled block of nav entries pinned to the bottom of the sider,
// typically the account rows.
export const bottomNavGroup = (key: string, children: AppShellMenuItem[]): AppShellMenuItem => ({
    key,
    type: "group",
    className: clsx(classes.navGroup, classes.accountGroup),
    children,
});

// A row inside a nav popover. Mirrors a nav entry, so the two read as one
// surface; see `.navPopoverAction`.
export const NavPopoverAction: React.FC<{
    icon?: React.ReactNode;
    onClick: () => void;
    children: React.ReactNode;
}> = ({ icon, onClick, children }) => (
    <button type="button" className={classes.navPopoverAction} onClick={onClick}>
        {icon}
        {children}
    </button>
);

// The whole row is the switch: it flips on a click anywhere on it or on
// Enter / Space, and the antd Switch inside is only its indicator.
export const NavPopoverToggle: React.FC<{
    checked: boolean;
    onChange: (checked: boolean) => void;
    children: React.ReactNode;
}> = ({ checked, onChange, children }) => (
    <div
        role="switch"
        aria-checked={checked}
        tabIndex={0}
        className={classes.navPopoverToggle}
        onClick={() => onChange(!checked)}
        onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
                event.preventDefault();
                onChange(!checked);
            }
        }}
    >
        {children}
        <Switch size="small" checked={checked} tabIndex={-1} />
    </div>
);

// A count at the far end of a nav entry, for things waiting on the user:
// new notifications, say. The entry's icon lights up with it (see
// .navBadge), so the nav says so at a glance. Nothing at zero.
export const NavBadge: React.FC<{ count: number }> = ({ count }) =>
    count > 0 ? <span className={classes.navBadge}>{count}</span> : null;

export const NavPopoverLabel: React.FC<{ children: React.ReactNode }> = ({ children }) => (
    <div className={classes.navPopoverLabel}>{children}</div>
);

// A row in a nav popover that opens a second panel of rows beside it on
// hover, for options that would otherwise crowd the first.
export const NavPopoverSubmenu: React.FC<{
    icon?: React.ReactNode;
    label: React.ReactNode;
    children: React.ReactNode;
}> = ({ icon, label, children }) => (
    <Popover
        trigger="hover"
        placement="rightTop"
        // Top edges level, overlapping the row by 8px as the nav popover
        // overlaps the sider.
        align={{ points: ["tl", "tr"], offset: [-8, 0] }}
        overlayClassName={classes.navPopover}
        content={children}
    >
        <div className={clsx(classes.navPopoverAction, classes.navPopoverSubmenuRow)}>
            {icon}
            {label}
            {/* SvgIcon wraps its svg in a span of its own, so the flex item
                that takes the margin has to be ours. */}
            <span className={classes.navPopoverChevron}>
                <SvgIcon name="right" size={8} />
            </span>
        </div>
    </Popover>
);

// Nav rows that open a popover instead of navigating: the sider is narrow, so
// their options live in a panel hinged on the row's bottom edge. Only one nav
// popover is open at a time, so a single key tracks them all. Actions inside a
// popover should call `closePopovers` before navigating.
export const useNavPopovers = () => {
    const [openPopover, setOpenPopover] = useState<string | null>(null);

    const closePopovers = useCallback(() => setOpenPopover(null), []);

    const popoverNavItem = (
        key: string,
        icon: React.ReactNode,
        label: React.ReactNode,
        content: React.ReactNode
    ): AppShellMenuItem => ({
        key,
        className: classes.popoverItem,
        icon,
        label: (
            <Popover
                open={openPopover === key}
                onOpenChange={(open) => setOpenPopover(open ? key : null)}
                trigger="click"
                placement="rightBottom"
                // antd's rightBottom hangs the popover off the trigger's centre
                // to leave room for an arrow we don't draw. Align the two bottom
                // edges instead, so the options stack upwards from the row, and
                // open 8px past the row's right edge so the panel overlaps the
                // sider's margin and reads as opening out of the nav. Measured
                // from the row (the trigger covers all of it), so it follows the
                // sider wherever it sits: a drawer, or shifted by the demo maker.
                align={{ points: ["bl", "br"], offset: [8, 0] }}
                overlayClassName={classes.navPopover}
                content={content}
            >
                <span className={classes.popoverTrigger}>
                    <span className={classes.popoverLabel}>{label}</span>
                </span>
            </Popover>
        ),
    });

    return { popoverNavItem, closePopovers };
};

interface AppShellContextValue {
    /** The stage: the scroll container the pages live in. */
    getContentContainer: () => HTMLElement | null;
    /**
     * The stage's frame: the same box, but it does not scroll, so an overlay
     * positioned in it stays put while the stage scrolls under it. For
     * panels opened over the stage (see NotificationsPanel).
     */
    getOverlayContainer: () => HTMLElement | null;
}

const AppShellContext = createContext<AppShellContextValue | null>(null);

interface AppShellProps {
    // Rendered at the top of the sider; typically a Link wrapping the product
    // logo, sized to the nav content column.
    logo?: React.ReactNode;
    // Shown in the compact shell's bar instead, where the sider logo is too
    // tall; the sider logo when not given.
    compactLogo?: React.ReactNode;
    menuItems: MenuProps["items"];
    selectedKeys?: string[];
    defaultOpenKeys?: string[];
    children: React.ReactNode;
}

// The app frame: a dark sider holding a logo and a nav menu, and a scrollable
// content stage. On a screen too narrow for both, the sider gives way to a
// drawer with the same nav, opened from a menu button in a bar on top. Owns
// no navigation of its own; menu entries, selection and their onClick
// handlers all come in through props, so any app can mount its own shell
// (see the item builders above for the supported entry shapes).
const AppShell: React.FC<AppShellProps> = ({
    logo,
    compactLogo = logo,
    menuItems,
    selectedKeys,
    defaultOpenKeys,
    children,
}) => {
    const frameRef = useRef<HTMLDivElement>(null);
    const contentRef = useRef<HTMLElement>(null);
    const compact = useMediaQuery(COMPACT_QUERY);
    const [navOpen, setNavOpen] = useState(false);

    // Navigating closes the drawer; so does the screen growing past compact.
    const { pathname } = useLocation();
    useEffect(() => setNavOpen(false), [pathname, compact]);

    const nav = (
        <>
            {logo && <div className={classes.logo}>{logo}</div>}
            <div className={classes.menuContainer}>
                <Menu
                    theme="dark"
                    mode="inline"
                    className={classes.menu}
                    selectedKeys={selectedKeys}
                    defaultOpenKeys={defaultOpenKeys}
                    expandIcon={({ isOpen }) => (
                        <SvgIcon
                            name={isOpen ? "up" : "down"}
                            className={"ant-menu-submenu-arrow"}
                        />
                    )}
                    items={menuItems}
                />
            </div>
        </>
    );

    const stage = (
        <div className={classes.stageFrame} ref={frameRef}>
            <Content className={classes.stage} ref={contentRef}>
                {children}
            </Content>
        </div>
    );

    return (
        <AppShellContext.Provider
            value={{
                getContentContainer: () => contentRef.current,
                getOverlayContainer: () => frameRef.current,
            }}
        >
            {compact ? (
                <Layout className={classes.container} style={{ minWidth: STAGE_MIN_WIDTH }}>
                    <div className={classes.compactBar}>
                        <button
                            type="button"
                            className={classes.menuButton}
                            aria-label="Open navigation"
                            aria-expanded={navOpen}
                            onClick={() => setNavOpen(true)}
                        >
                            <SvgIcon name="menu" size={20} aria-hidden="true" />
                        </button>
                        {compactLogo && <div className={classes.logo}>{compactLogo}</div>}
                    </div>
                    {stage}
                    <Drawer
                        open={navOpen}
                        onClose={() => setNavOpen(false)}
                        placement="left"
                        width={NAV_WIDTH}
                        closable={false}
                        className={classes.drawer}
                    >
                        {nav}
                    </Drawer>
                </Layout>
            ) : (
                <Layout className={classes.container}>
                    <Sider theme="dark" className={classes.sider} width={NAV_WIDTH}>
                        {nav}
                    </Sider>
                    {stage}
                </Layout>
            )}
        </AppShellContext.Provider>
    );
};

export const useAppShellContext = (): AppShellContextValue => {
    const context = useContext(AppShellContext);
    if (!context) {
        throw new Error("useAppShellContext must be used within an AppShell");
    }
    return context;
};

export default AppShell;
